/**
 * Plain HTML table styled like the MUI table, for the permissions matrix.
 * Copied from import-hub, which keeps its own copy for the same reason: there is no table in tw-components yet.
 *
 * TODO: replace with the tw-components table once it exists.
 */
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type CSSProperties,
} from 'react';

import { typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

export const Table = forwardRef<
  HTMLTableElement,
  ComponentPropsWithoutRef<'table'>
>(({ className, ...props }, ref) => (
  <table
    ref={ref}
    className={cn(
      'w-full border-separate border-spacing-0 bg-ui-primary',
      typographyVariants({ variant: 'bodyShort01', color: 'primary' }),
      className,
    )}
    {...props}
  />
));
Table.displayName = 'Table';

export const TableHead = forwardRef<
  HTMLTableSectionElement,
  ComponentPropsWithoutRef<'thead'>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('bg-ui-primary', className)} {...props} />
));
TableHead.displayName = 'TableHead';

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  ComponentPropsWithoutRef<'tbody'>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={className} {...props} />
));
TableBody.displayName = 'TableBody';

export const TableRow = forwardRef<
  HTMLTableRowElement,
  ComponentPropsWithoutRef<'tr'> & { hover?: boolean }
>(({ className, hover, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'group/table-row bg-ui-primary',
      hover && 'hover:bg-ui-hover',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

type TableCellAlignment = 'center' | 'left' | 'right';
type TableCellLevel = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

const alignmentClasses: Record<TableCellAlignment, string> = {
  center: 'justify-center text-center',
  left: 'justify-start text-left',
  right: 'justify-end text-right',
};

export type TableBodyCellProps = Omit<
  ComponentPropsWithoutRef<'td'>,
  'align'
> & {
  align?: TableCellAlignment;
  /** Indents nested rows and draws the tree line on the left, like MUI. */
  level?: TableCellLevel;
  /** Stops the tree line halfway, for the last child of a group. */
  levelIsLast?: boolean;
};

export const TableBodyCell = forwardRef<
  HTMLTableCellElement,
  TableBodyCellProps
>(
  (
    {
      align = 'left',
      children,
      className,
      level = 0,
      levelIsLast = false,
      style,
      ...props
    },
    ref,
  ) => (
    <td
      ref={ref}
      className={cn(
        'relative box-border min-h-10 border-t border-border-secondary px-4 align-middle',
        level > 0 &&
          'before:absolute before:top-0 before:left-6 before:w-px before:bg-ui-tertiary before:content-[""]',
        level > 0 && (levelIsLast ? 'before:h-1/2' : 'before:h-full'),
        className,
      )}
      style={{ '--table-cell-level': `${level}rem`, ...style } as CSSProperties}
      {...props}
    >
      <div
        className={cn(
          'flex min-h-10 w-full items-center gap-2',
          alignmentClasses[align],
        )}
      >
        {level > 0 && (
          <span
            aria-hidden
            className="ml-2 h-px w-(--table-cell-level) shrink-0 bg-ui-tertiary"
          />
        )}
        {children}
      </div>
    </td>
  ),
);
TableBodyCell.displayName = 'TableBodyCell';

export const TableHeaderCell = forwardRef<
  HTMLTableCellElement,
  Omit<ComponentPropsWithoutRef<'th'>, 'align'> & { align?: TableCellAlignment }
>(({ align = 'left', children, className, ...props }, ref) => (
  <th
    ref={ref}
    scope="col"
    className={cn(
      'box-border h-10 bg-ui-primary px-4 align-middle',
      typographyVariants({ variant: 'h6', color: 'primary' }),
      className,
    )}
    {...props}
  >
    <div
      className={cn('flex min-w-0 items-center gap-2', alignmentClasses[align])}
    >
      {children}
    </div>
  </th>
));
TableHeaderCell.displayName = 'TableHeaderCell';
